import { roundToTwoDecimals } from 'common/helpers/';

export const SCORE_WEIGHTS = {
  videoFreezing: 0.3, // Highest - completely breaks communication experience
  packetLoss: 0.25, // Second highest - affects overall quality
  jitter: 0.2, // Third - very noticeable to users
  frameRate: 0.15, // Fourth - affects smoothness
  rtt: 0.05, // Lower - less noticeable in video calls
  frameDrops: 0.05, // Lowest - often correlates with other issues
};

export const scorePacketLossPercent = (packetLossRate: number): number => {
  if (packetLossRate <= 0) {
    return 100;
  }
  // Each band linearly interpolates from its ceiling score down to its floor score.
  // The multiplier is the slope: how many score points to subtract per 1% of packet loss.
  // Derived as: (ceiling_score - floor_score) / band_width_percent
  //
  if (packetLossRate <= 0.03) {
    return Math.max(93, 100 - packetLossRate * 233);
  }
  if (packetLossRate <= 0.1) {
    return Math.max(78, 93 - (packetLossRate - 0.03) * 214);
  }
  if (packetLossRate <= 0.5) {
    return Math.max(60, 78 - (packetLossRate - 0.1) * 45);
  }
  if (packetLossRate <= 1) {
    return Math.max(35, 60 - (packetLossRate - 0.5) * 50);
  }
  if (packetLossRate <= 2) {
    return Math.max(0, 35 - (packetLossRate - 1) * 35);
  }

  return 0;
};

export const calculateJitterScore = (jitter: number): number => {
  // Convert jitter from seconds to milliseconds, ensure non-negative
  const jitterMs = Math.max(0, jitter) * 1000;

  if (jitterMs <= 5) {
    return 100;
  }
  if (jitterMs <= 15) {
    return Math.max(80, 100 - (jitterMs - 5) * 2);
  }
  if (jitterMs <= 30) {
    return Math.max(60, 80 - (jitterMs - 15) * 1.33);
  }
  if (jitterMs <= 60) {
    return Math.max(35, 60 - (jitterMs - 30) * 0.83);
  }

  return Math.max(0, 35 - (jitterMs - 60) * 0.29);
};

export const getGrade = (score: number): string => {
  if (score >= 90) {
    return 'A';
  }
  if (score >= 80) {
    return 'B';
  }
  if (score >= 70) {
    return 'C';
  }
  if (score >= 60) {
    return 'D';
  }
  return 'F';
};

export const toFiniteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

// Far-end RTCP jitter above 1s with a working call is a bogus report (e.g. a timestamp jump at an SBC).
export const MAX_PLAUSIBLE_REMOTE_JITTER_SECONDS = 1;

export const isPlausibleRemoteJitter = (jitterSeconds: number): boolean =>
  jitterSeconds >= 0 && jitterSeconds <= MAX_PLAUSIBLE_REMOTE_JITTER_SECONDS;

export const toPlausibleRemoteJitter = (value: unknown): number | undefined => {
  const jitter = toFiniteNumber(value);
  return jitter !== undefined && isPlausibleRemoteJitter(jitter)
    ? jitter
    : undefined;
};

export const toPercent = (part: number, total: number): number | undefined =>
  total > 0 ? roundToTwoDecimals((Math.max(0, part) / total) * 100) : undefined;

// Frames ÷ FPS is the only duration proxy; there is no reliable elapsed-time source for the call.
export const estimateCallDurationSeconds = (
  framesDecoded = 0,
  framesReceived = 0,
  frameRate = 0
): number =>
  frameRate > 0 ? Math.max(framesDecoded, framesReceived, 0) / frameRate : 0;
